import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { parse } from 'query-string';
import classNames from 'classnames/bind';

import {
  DataBlocks,
  I18n,
  ErrorBlock,
  Button,
  Heading,
  Preloader,
  RouterLink,
} from 'components';

import {
  formatDate,
  formatUnitString,
} from 'utils/formatString';

import {
  fetchCtnDetalizationInitialData,
  toggleExpensesFilter,
  toggleOnlyPaidFilter,
  saveDetalizationForCtn,
  stopDownloading,
} from '../../actions';

import { EXPENSES_SECTIONS } from '../../constants';

import ExpensesTabs from '../../components/ExpensesTabs';
import TotalSpent from '../../components/TotalSpent';
import SaveDetalizationPopup from '../../components/SaveDetalizationPopup';
import SaveDetalization from '../../components/SaveDetalization';

import styles from './styles.pcss';

const cx = classNames.bind(styles);

class CtnDetalization extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      sendToEmail: true,
      isPopupOpened: false,
      activeTab: EXPENSES_SECTIONS.STRUCTURE,
    };
  }

  componentDidMount() {
    const {
      actions,
      location,
    } = this.props;

    actions.fetchCtnDetalizationInitialData(parse(location.search));
  }

  setActiveTab = (tab) => {
    this.setState({ activeTab: tab });
  };

  render() {
    const {
      transactions,
      error,
      structure,
      actions,
      filters,
      onlyFreeActions,
      location,
      startDate,
      endDate,
      currency,
      vat,
      isEmailConfirmed,
      isReportSending,
      reportSendingError,
      expensesAmount,
      ctnFor,
      tooManyTransactions,
      showBackLink,
      isFetching,
      settings,
      amount,
    } = this.props;

    const {
      sendToEmail,
      activeTab,
      isPopupOpened,
    } = this.state;

    if (error) {
      return (
        <ErrorBlock
          title={<I18n path="detalization.blockTitle" />}
          refreshAction={() => actions.fetchCtnDetalizationInitialData(parse(location.search))}
        />
      );
    }

    return (
      <DataBlocks.Container>
        <DataBlocks.Block
          isLoading={isFetching}
          title={<I18n path="detalization.blockTitle" />}
        >
          {showBackLink && (
            <RouterLink
              className="link--hidden-visited-color"
              to="/detalization"
              exact
            >
              <I18n path="detalization.back" />
            </RouterLink>
          )}
          <DataBlocks.ContentGroup
            title={(
              <>
                <I18n
                  className={cx('title')}
                  path="detalization.postpaidInvoices.ctnTitle"
                  vars={{ start: formatDate(startDate), end: formatDate(endDate) }}
                />
                <div>
                  <div className={cx('expensesAmount')}>
                    {formatUnitString(expensesAmount, currency)}
                  </div>
                  <Button
                    className="default"
                    onClick={() => this.setState({
                      isPopupOpened: true,
                    })}
                  >
                    <I18n path="detalization.postpaidInvoices.invoice.saveReport" />
                  </Button>
                </div>
              </>
            )}
          >
            {tooManyTransactions ? (
              <>
                {isReportSending ? (
                  <Preloader className={cx('preloader')} size="80" />
                ) : (
                  <div className={cx('tooManyTransactionsMessage')}>
                    <Heading className={cx('tooManyTransactionsTitle')} level={4}>
                      <I18n path="detalization.expenses.tooManyTransactionsTitle" />
                    </Heading>
                    <SaveDetalization
                      sendToEmail={sendToEmail}
                      isEmailConfirmed={isEmailConfirmed}
                      toggleSendToEmail={() => this.setState({
                        sendToEmail: !sendToEmail,
                      })}
                      saveReport={() => actions.saveDetalizationForCtn({
                        ctnFor,
                        isEmailSend: isEmailConfirmed && sendToEmail,
                      }, true)}
                      isSSO={
                        settings.masterLogin
                        && settings.selectedLogin
                        && settings.selectedLogin !== settings.masterLogin
                      }
                    />
                  </div>
                )}
              </>
            ) : (
              <ExpensesTabs
                currency={currency}
                filters={filters}
                onlyFreeActions={onlyFreeActions}
                details={transactions}
                spendingStructure={structure}
                toggleFilter={actions.toggleExpensesFilter}
                toggleOnlyPaidFilter={actions.toggleOnlyPaidFilter}
                postpaid
                onChangeActiveTab={this.setActiveTab}
              />
            )}
            {!tooManyTransactions && activeTab === EXPENSES_SECTIONS.STRUCTURE && (
              <TotalSpent
                structure={structure}
                filters={filters}
                currency={currency}
                vat={vat}
                amount={amount}
              />
            )}
            <SaveDetalizationPopup
              isOpened={isPopupOpened}
              closePopup={() => this.setState({
                isPopupOpened: false,
              })}
              stopDownloading={() => {
                actions.stopDownloading();
                this.setState({ isPopupOpened: false });
              }}
              startDate={startDate}
              endDate={endDate}
              isEmailConfirmed={isEmailConfirmed}
              saveReport={actions.saveDetalizationForCtn}
              isSaving={isReportSending}
              savingError={reportSendingError}
              ctnFor={ctnFor}
            />
          </DataBlocks.ContentGroup>
        </DataBlocks.Block>
      </DataBlocks.Container>
    );
  }
}

function mapStateToProps(state) {
  return {
    ...state.detalization.expenses,
    isEmailConfirmed: state.detalization.email.isConfirmed,
    showBackLink: !state.detalization.invoices.isEmpty,
    settings: state.application.settings,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({
      fetchCtnDetalizationInitialData,
      toggleExpensesFilter,
      toggleOnlyPaidFilter,
      saveDetalizationForCtn,
      stopDownloading,
    }, dispatch),
  };
}

CtnDetalization.propTypes = {
  transactions: PropTypes.array,
  filters: PropTypes.array,
  structure: PropTypes.array,
  error: PropTypes.string,
  actions: PropTypes.object,
  location: PropTypes.object,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  currency: PropTypes.string,
  vat: PropTypes.number,
  isEmailConfirmed: PropTypes.bool,
  isReportSending: PropTypes.bool,
  reportSendingError: PropTypes.string,
  expensesAmount: PropTypes.number,
  ctnFor: PropTypes.string,
  tooManyTransactions: PropTypes.bool,
  showBackLink: PropTypes.bool,
  isFetching: PropTypes.bool,
  settings: PropTypes.object,
  onlyFreeActions: PropTypes.bool,
  amount: PropTypes.number,
};

export default connect(mapStateToProps, mapDispatchToProps)(CtnDetalization);
